import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import WhiteboardCanvas from '../../../components/WhiteboardCanvas';
import ProblemMarkdown from './ProblemMarkdown';

const getWorkspaceStorageKey = (sessionId) => `prepxai_interview_workspace_${sessionId}`;

const readWorkspaceState = (sessionId) => {
  if (!sessionId || typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(getWorkspaceStorageKey(sessionId)) || '{}');
  } catch (error) {
    return {};
  }
};

const getProblemKey = (problem) => String(
  problem?.id || problem?.title || problem?.question || 'waiting-for-problem'
);

const persistCodeDraft = (sessionId, problemKey, language, code) => {
  if (!sessionId || typeof localStorage === 'undefined') return;
  try {
    const stored = readWorkspaceState(sessionId);
    localStorage.setItem(getWorkspaceStorageKey(sessionId), JSON.stringify({
      ...stored,
      selectedLanguage: language,
      drafts: {
        ...(stored.drafts || {}),
        [problemKey]: {
          ...(stored.drafts?.[problemKey] || {}),
          [language]: code,
        },
      },
      latest: { code, language, problemKey },
    }));
  } catch (error) {
    // The WebSocket still receives edits when storage is unavailable or full.
  }
};

const UnifiedWorkspace = ({
  problemData,
  problemStatement,
  interviewType,
  sessionId,
  onCodeChange,
  onCodeSubmit,
  onCodeRun,
  onWhiteboardUpdate,
  executionResult,
  isExecuting,
  isEnding,
  onInternalCopy
}) => {
  const [activeTab, setActiveTab] = useState('code'); // 'code', 'whiteboard', 'testcases'
  const [selectedLanguage, setSelectedLanguage] = useState(
    () => readWorkspaceState(sessionId).selectedLanguage || 'javascript'
  );
  const [code, setCode] = useState('');

  // Resizable state
  const [leftPanelWidth, setLeftPanelWidth] = useState(40);
  const [isResizing, setIsResizing] = useState(false);

  const editorRef = React.useRef(null);
  const [whiteboardSnapshot, setWhiteboardSnapshot] = React.useState(null);

  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' }
  ];

  const [editorTheme, setEditorTheme] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light'
  );

  useEffect(() => {
    const updateTheme = () => {
      setEditorTheme(document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light');
    };

    const observer = new MutationObserver(() => updateTheme());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const problem = problemData || {
    title: problemStatement || 'Waiting for Problem...',
    difficulty: problemData?.difficulty || 'Medium',
    description: problemData?.question || problemStatement || 'Please wait for the interviewer to provide the problem statement.',
    examples: problemData?.examples || problemData?.Examples || [],
    constraints: problemData?.constraints || [],
    hints: problemData?.hints || []
  };

  const problemExamples = problem?.examples || problem?.Examples || [];
  const problemTestCases = problem?.test_cases || problem?.TestCases || [];

  const defaultCode = {
    javascript: `// Write your solution here\n`,
    python: `# Write your solution here\npass`,
    java: `public class Solution {\n    public void solve() {\n        // Write your solution here\n    }\n}`,
    cpp: `#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n    // Write your solution here\n\n    return 0;\n}`
  };

  // Initialize Code
  useEffect(() => {
    const problemKey = getProblemKey(problem);
    const storedDraft = readWorkspaceState(sessionId).drafts?.[problemKey]?.[selectedLanguage];
    const boilerSnippet = problem?.boilerplates?.[selectedLanguage] || problem?.boilerplate;
    const nextCode = typeof storedDraft === 'string'
      ? storedDraft
      : (boilerSnippet || defaultCode?.[selectedLanguage] || '');
    setCode(nextCode);
    persistCodeDraft(sessionId, problemKey, selectedLanguage, nextCode);
  }, [problem?.id, problem?.title, problem?.question, selectedLanguage, sessionId]);

  // Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = (e.clientX / window.innerWidth) * 100;
        if (newWidth > 20 && newWidth < 80) setLeftPanelWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.userSelect = 'auto';
    };
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'auto';
    };
  }, [isResizing]);

  // Whiteboard snapshot sync
  const handleWhiteboardChange = (elements, meta = {}) => {
    if (isEnding) return;
    const str = JSON.stringify(elements);
    setWhiteboardSnapshot(str);
    if (onWhiteboardUpdate) {
      onWhiteboardUpdate({
        state: str,
        image: meta.imageDataUrl || '',
        image_mime_type: meta.imageMimeType || 'image/jpeg',
      });
    }
  };

  const handleEditorChange = (value) => {
    const nextCode = value || '';
    setCode(nextCode);
    persistCodeDraft(sessionId, getProblemKey(problem), selectedLanguage, nextCode);
    if (onCodeChange) onCodeChange(nextCode, selectedLanguage);
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Track copy-from-IDE natively without breaking system clipboard
    const domNode = editor.getDomNode();
    if (domNode) {
      domNode.addEventListener('copy', () => {
        const selection = editor.getSelection();
        const model = editor.getModel();
        if (selection && model && !selection.isEmpty()) {
          const text = model.getValueInRange(selection);
          if (onInternalCopy) onInternalCopy(text);
        }
      });
    }
  };

  return (
    <div className="flex h-full w-full bg-slate-100/70 dark:bg-[#17181e] text-foreground">
      {/* Left Panel: Problem */}
      <div
        data-question-content="true"
        className="flex flex-col h-full bg-white dark:bg-card flex-shrink-0 select-none"
        style={{ width: `${leftPanelWidth}%`, userSelect: 'none' }}
      >
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 custom-scrollbar">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xl font-bold text-foreground leading-tight">
              {problem.title}
            </h2>
            <div className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider ${problem.difficulty === 'Easy' ? 'bg-emerald-500/10 text-primary' :
                problem.difficulty === 'Medium' ? 'bg-yellow-500/10 text-yellow-500' :
                  'bg-red-500/10 text-red-500'
              }`}>
              {problem.difficulty}
            </div>
          </div>

          <ProblemMarkdown className="prose prose-sm max-w-none text-foreground font-normal leading-relaxed space-y-4 prose-p:my-1">
            {problem.description || problem.question || 'No problem description available.'}
          </ProblemMarkdown>

          {problemExamples?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-bold text-foreground tracking-wider mb-4">Examples</h3>
              <div className="space-y-4">
                {problemExamples.map((ex, i) => (
                  <div key={i} className="bg-muted rounded-2xl p-5">
                    <p className="text-xs font-bold text-black dark:text-slate-200 mb-2 uppercase">Example {i + 1}</p>
                    <div className="font-mono text-xs text-black dark:text-slate-200 space-y-1">
                      {(ex.input || ex.Input) && <p><span className="text-black dark:text-slate-300 font-sans font-medium">Input:</span> {ex.input || ex.Input}</p>}
                      {(ex.output || ex.Output) && <p><span className="text-black dark:text-slate-300 font-sans font-medium">Output:</span> {ex.output || ex.Output}</p>}
                      {(ex?.explanation || ex?.explaination || ex?.Explanation) && (
                        <div className="mt-2 font-sans">
                          {(ex.input || ex.Input || ex.output || ex.Output) && <span className="font-medium block mb-1">Explanation:</span>}
                          <ProblemMarkdown className="text-black dark:text-slate-300 leading-relaxed text-[11px] prose prose-sm max-w-none prose-pre:bg-slate-900 prose-pre:text-slate-100">
                            {ex?.explanation || ex?.explaination || ex?.Explanation}
                          </ProblemMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {problem.constraints?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-bold text-foreground tracking-wider mb-3">Constraints</h3>
              <ul className="list-disc pl-5 space-y-1.5 text-slate-300 font-mono text-sm leading-relaxed">
                {problem.constraints.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        className="w-2 bg-slate-100/80 dark:bg-[#17181e] hover:bg-primary/10 cursor-col-resize flex-shrink-0 transition-colors z-10"
        onMouseDown={() => setIsResizing(true)}
      />

      {/* Right Panel: Workspace Tabs (Code/Whiteboard/Testcases) */}
      <div
        className="flex flex-col h-full bg-white dark:bg-card relative flex-shrink-0"
        style={{ width: `calc(${100 - leftPanelWidth}% - 8px)` }}
      >
        {/* Top Tab Bar */}
        <div className="h-9 px-4 flex items-center justify-between bg-slate-50/80 dark:bg-muted/50 flex-shrink-0">
          <div className="flex items-center gap-4 h-full">
            <button
              onClick={() => setActiveTab('code')}
              className={`h-full px-0 text-[12px] font-semibold transition-all relative ${activeTab === 'code' ? 'text-primary' : 'text-slate-400 hover:text-foreground'}`}
            >
              Code
              {activeTab === 'code' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full" />}
            </button>
            <button
              onClick={() => setActiveTab('whiteboard')}
              className={`h-full px-0 text-[12px] font-semibold transition-all relative ${activeTab === 'whiteboard' ? 'text-primary' : 'text-slate-400 hover:text-foreground'}`}
            >
              Board
              {activeTab === 'whiteboard' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full" />}
            </button>
            <button
              onClick={() => setActiveTab('testcases')}
              className={`h-full px-0 text-[12px] font-semibold transition-all relative ${activeTab === 'testcases' ? 'text-primary' : 'text-slate-400 hover:text-foreground'}`}
            >
              Test Cases
              {activeTab === 'testcases' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full" />}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'code' && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="h-6 text-[11px] font-bold text-foreground bg-muted border border-border rounded-md px-2 py-0.5 outline-none focus:border-primary custom-select"
                >
                  {languages.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>

                <button
                  onClick={() => {
                    setActiveTab('testcases');
                    if (onCodeRun) onCodeRun(code, selectedLanguage, '');
                  }}
                  disabled={isExecuting}
                  className="h-6 px-2.5 py-0 bg-primary hover:bg-primary/95 text-primary-foreground rounded-md text-[11px] font-bold shadow-sm flex items-center gap-1 disabled:opacity-50 transition-colors"
                >
                  {isExecuting ? <div className="w-3 h-3 border-2 border-primary-foreground rounded-full border-t-transparent animate-spin" /> : <Icon name="Play" size={12} />}
                  RUN
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 relative overflow-hidden bg-muted">
          {activeTab === 'code' && (
            <Editor
              height="100%"
              language={selectedLanguage === 'cpp' ? 'cpp' : selectedLanguage}
              value={code}
              onChange={handleEditorChange}
              onMount={handleEditorMount}
              theme={editorTheme}
              options={{
                fontSize: 14,
                fontFamily: "'Inter', 'Fira Code', monospace",
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                lineHeight: 24,
                padding: { top: 12, bottom: 12 },
                renderLineHighlight: 'gutter',
                overviewRulerBorder: false,
                hideCursorInOverviewRuler: true
              }}
            />
          )}

          {activeTab === 'whiteboard' && (
            <div
              key={`whiteboard-container-${sessionId || 'default'}`}
              className="absolute inset-0"
              style={{ zIndex: 10 }}
            >
              <WhiteboardCanvas
                snapshot={whiteboardSnapshot}
                onSnapshotChange={handleWhiteboardChange}
              />
            </div>
          )}

          {activeTab === 'testcases' && (
            <div className="p-5 h-full overflow-y-auto bg-background custom-scrollbar">
              <div className="max-w-3xl space-y-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-sm font-bold text-foreground tracking-wider">Test Cases</h3>
                  {executionResult?.passed_count !== undefined && (
                    <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-md ${executionResult.passed_count === executionResult.total_count ? 'bg-emerald-500/10 text-primary' : 'bg-red-500/10 text-red-500'}`}>
                            {executionResult.passed_count} / {executionResult.total_count} Passed
                        </span>
                    </div>
                  )}
                </div>

                {isExecuting ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-8 h-8 border-4 border-primary rounded-full border-t-transparent animate-spin" />
                    <p className="text-slate-400 font-medium">Executing code against test cases...</p>
                  </div>
                ) : (executionResult?.results || executionResult?.stdout || executionResult?.stderr) ? (
                  <div className="space-y-4">
                    {/* Structured Results (if available) */}
                    {executionResult?.results ? (
                      executionResult.results.map((res, idx) => (
                        <div key={idx} className={`bg-card rounded-2xl border ${res.passed ? 'border-emerald-500/20' : 'border-red-500/20'} overflow-hidden shadow-sm`}>
                          <div className={`px-5 py-4 flex items-center justify-between ${res.passed ? 'bg-emerald-500/5' : 'bg-red-500/5'} border-b border-border`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${res.passed ? 'bg-emerald-500' : 'bg-red-500'}`} />
                              <span className="font-bold text-foreground text-sm">
                                Test Case {idx + 1} {res.is_hidden && <span className="text-[10px] bg-muted text-slate-400 px-1.5 py-0.5 rounded ml-2 uppercase">Hidden</span>}
                              </span>
                            </div>
                            <span className={`text-[10px] font-bold tracking-widest ${res.passed ? 'text-primary' : 'text-red-500'}`}>
                              {res.status}
                            </span>
                          </div>
                          
                          {/* Only show details for non-hidden or if it's an error */}
                          <div className="p-5 space-y-4">
                            {!res.is_hidden ? (
                              <>
                                <div>
                                  <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Input:</p>
                                  <pre className="bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100 border border-border whitespace-pre-wrap">{res.input}</pre>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Expected:</p>
                                    <pre className="bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100 border border-border whitespace-pre-wrap">{res.expected}</pre>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Actual:</p>
                                    <pre className={`bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm ${res.passed ? 'text-primary' : 'text-red-500 dark:text-red-400'} border border-border whitespace-pre-wrap`}>{res.actual || '(no output)'}</pre>
                                  </div>
                                </div>
                                {res.stdout && res.stdout.trim() !== res.actual && (
                                  <div>
                                    <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Full Stdout:</p>
                                    <pre className="bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100 border border-border whitespace-pre-wrap">{res.stdout}</pre>
                                  </div>
                                )}
                                {res.stderr && (
                                  <div>
                                    <p className="text-[10px] font-bold text-amber-500 tracking-widest mb-1.5">Stderr:</p>
                                    <pre className="bg-amber-500/5 rounded-xl px-4 py-3 font-mono text-sm text-amber-400 border border-amber-500/20 whitespace-pre-wrap">{res.stderr}</pre>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-slate-400 italic">Details are hidden for this test case.</p>
                            )}
                            
                            {res.error && (
                              <div className="mt-2">
                                <p className="text-[10px] font-bold text-red-400 tracking-widest mb-1.5">Error:</p>
                                <pre className="bg-red-500/5 rounded-xl px-4 py-3 font-mono text-sm text-red-400 border border-red-500/20 whitespace-pre-wrap">{res.error}</pre>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      /* Legacy Fallback */
                      <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                        {executionResult.stdout && (
                          <div className="mb-4">
                            <span className="text-[10px] font-bold text-muted-foreground tracking-widest block mb-2">Standard Output</span>
                            <pre className="text-sm text-slate-900 dark:text-slate-100 font-mono whitespace-pre-wrap">{executionResult.stdout}</pre>
                          </div>
                        )}
                        {executionResult.stderr && (
                          <div className="mb-4">
                            <span className="text-[10px] font-bold text-red-400 tracking-widest block mb-2">Standard Error</span>
                            <pre className="text-sm text-red-400 font-mono whitespace-pre-wrap">{executionResult.stderr}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Initial State: Show Visible Test Cases from problemData */}
                    {(problemTestCases?.length > 0 ? problemTestCases : problemExamples).filter(tc => !tc.is_hidden).map((tc, idx) => (
                      <div key={idx} className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
                        <div className="px-5 py-4 flex items-center justify-between bg-muted border-b border-border">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-slate-500" />
                            <span className="font-bold text-foreground text-sm">Test Case {idx + 1}</span>
                          </div>
                        </div>
                        <div className="p-5 space-y-4">
                          <div>
                            <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Input:</p>
                            <div className="bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100 border border-border whitespace-pre-wrap">
                              {tc.input}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-muted-foreground tracking-widest mb-1.5">Expected Output:</p>
                            <div className="bg-slate-100/80 dark:bg-muted rounded-xl px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100 border border-border whitespace-pre-wrap">
                              {tc.output}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!problemTestCases?.length && !problemExamples?.length) && (
                        <p className="text-sm text-slate-400 italic text-center py-12">No visible test cases available for this question.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedWorkspace;
